
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Eye, X, Heart } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "@/contexts/CartContext";
import { useWishlist } from "@/contexts/WishlistContext";

interface ProductCardProps {
  id: string;
  name: string;
  price: number;
  image: string;
  category: string;
  description: string;
  ingredients?: string;
  rating?: number;
  reviews?: number;
  type?: string;
  isBestSeller?: boolean;
  isNew?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({
  id,
  name,
  price,
  image,
  category,
  description,
  ingredients,
  type,
  isBestSeller,
  isNew,
}) => {
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const { addToCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const wishlisted = isInWishlist(id);

  const handleToggleWishlist = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    toggleWishlist({ id, name, price, image, category, description });
  };

  const handleAddToCart = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    addToCart({ id, name, price, image });
  };

  return (
    <>
      <motion.div
        className="bg-white rounded-lg overflow-hidden shadow-sm border border-bunnie-brown/5 group relative"
        whileHover={{ y: -6, boxShadow: "0 12px 30px -8px rgba(90, 60, 40, 0.15)" }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        <Link to={`/product/${id}`} className="block relative">
          <div className="w-full h-48 bg-bunnie-cream/30 flex items-center justify-center overflow-hidden">
            <motion.img
              src={image}
              alt={name}
              className="w-full h-full object-contain p-2"
              loading="lazy"
              width="600"
              height="600"
              whileHover={{ scale: 1.08 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
          </div>
          {(isBestSeller || isNew) && (
            <Badge
              className="absolute top-3 right-3 bg-bunnie-peach text-bunnie-brown font-medium"
              variant="secondary"
            >
              {isNew ? "New" : "Best Seller"}
            </Badge>
          )}
        </Link>

        {/* Wishlist heart - always visible */}
        <motion.button
          onClick={handleToggleWishlist}
          className="absolute top-3 left-3 z-10 p-2 rounded-full bg-white/80 backdrop-blur-sm shadow-sm hover:bg-white transition-colors"
          whileHover={{ scale: 1.15 }}
          whileTap={{ scale: 0.9 }}
          aria-label={wishlisted ? "Remove from wishlist" : "Add to wishlist"}
        >
          <Heart className={`h-4 w-4 transition-colors ${wishlisted ? "fill-red-400 text-red-400" : "text-bunnie-brown/60"}`} />
        </motion.button>

        {/* Hover actions */}
        <motion.div
          className="absolute inset-x-0 top-0 h-48 flex items-end justify-center pb-3 gap-3 pointer-events-none"
          initial={{ opacity: 0 }}
          whileHover={{ opacity: 1 }}
        >
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex gap-3 pointer-events-auto">
            <motion.button
              onClick={(e) => { e.preventDefault(); setQuickViewOpen(true); }}
              className="p-2.5 bg-white/90 backdrop-blur-sm rounded-full text-bunnie-brown shadow-md hover:bg-bunnie-peach/30 transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Quick view"
            >
              <Eye className="h-4 w-4" />
            </motion.button>
            <motion.button
              onClick={handleAddToCart}
              className="p-2.5 bg-white/90 backdrop-blur-sm rounded-full text-bunnie-brown shadow-md hover:bg-bunnie-peach/30 transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Add to bag"
            >
              <ShoppingBag className="h-4 w-4" />
            </motion.button>
          </div>
        </motion.div>

        <div className="p-4 text-center">
          <Link to={`/product/${id}`} className="block">
            <h3 className="font-medium text-bunnie-brown hover:text-opacity-80 transition-colors text-center mb-1">{name}</h3>
          </Link>
          <p className="text-bunnie-brown/60 text-sm mb-2 text-center">{category}</p>
          <p className="text-bunnie-brown/70 text-xs mb-3 line-clamp-2 text-center">{description}</p>
          <div className="flex justify-center items-center gap-3">
            <p className="text-bunnie-brown font-serif font-semibold">${price.toFixed(2)}</p>
            <motion.button
              onClick={handleAddToCart}
              className="p-2 bg-bunnie-brown text-white rounded-full hover:bg-bunnie-brown/80 transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              aria-label="Add to cart"
            >
              <ShoppingBag className="h-4 w-4" />
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Quick View Modal */}
      <AnimatePresence>
        {quickViewOpen && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={() => setQuickViewOpen(false)}
            />
            <motion.div
              className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto z-10"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <button
                onClick={() => setQuickViewOpen(false)}
                className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-bunnie-cream/80 hover:bg-bunnie-cream text-bunnie-brown transition-colors"
                aria-label="Close quick view"
              >
                <X className="h-4 w-4" />
              </button>
              <div className="p-0">
                <div className="w-full h-64 bg-bunnie-cream/30 flex items-center justify-center rounded-t-xl overflow-hidden">
                  <img src={image} alt={name} className="w-full h-full object-contain p-4" />
                </div>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-bunnie-brown/50 text-xs uppercase tracking-wider mb-1">{category}{type ? ` · ${type}` : ""}</p>
                      <h3 className="font-serif text-xl text-bunnie-brown font-medium">{name}</h3>
                    </div>
                    <p className="text-bunnie-brown font-serif font-semibold text-lg">${price.toFixed(2)}</p>
                  </div>
                  <p className="text-bunnie-brown/70 text-sm mb-4">{description}</p>
                  {ingredients && (
                    <div className="mb-5">
                      <h4 className="text-xs font-medium text-bunnie-brown uppercase tracking-wider mb-1">Key Ingredients</h4>
                      <p className="text-bunnie-brown/60 text-xs leading-relaxed">{ingredients}</p>
                    </div>
                  )}
                  <div className="flex gap-3">
                    <Button
                      onClick={() => { handleAddToCart(); setQuickViewOpen(false); }}
                      className="flex-1 bg-bunnie-brown text-white hover:bg-bunnie-brown/90"
                    >
                      <ShoppingBag className="h-4 w-4 mr-2" /> Add to Cart
                    </Button>
                    <Link to={`/product/${id}`} className="flex-1" onClick={() => setQuickViewOpen(false)}>
                      <Button variant="outline" className="w-full border-bunnie-brown text-bunnie-brown hover:bg-bunnie-brown hover:text-white">
                        View Details
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ProductCard;
